//@flow
import React, {useEffect, useRef, useState} from 'react';

import {
  CircularProgress,
  MenuItem,
  // MenuItem,
  Table,
  TableBody,
  TableContainer,
  TableHead,
} from '@material-ui/core';
import {Add} from '@material-ui/icons';
import {makeStyles} from '@material-ui/styles';
import PageHeader from '../../../elements/PageHeader';

import SearchBar from '../../../elements/SearchBar/SearchBar';

import {colors} from '../../../assets/styles/colors';
import ProductFilter from './ProductFilter';

import ProductRow from './ProductRow';
import TableCellFilter from './TableCellFilter';
import BulkEditOptions from './BulkEditOptions';
import {TableCellProvider} from '../context/CellContext';
import TableHeader from './TableHeader';
import {useFetchProducts, useProductPagination, useProducts} from '../utils';
import {ProductProvider} from '../context';

import {Pagination} from '@material-ui/lab';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import CustomHorizontalScroll from '../../../elements/CustomHorizontalScroll';
// import CustomSelect from '../../../elements/CustomSelect/CustomSelect';

import type {Product} from '../../../entities';
import CustomSelect from '../../../elements/CustomSelect/CustomSelect';
import BulkEditToast from './BulkEditToast';
import {useTierRestrictionActions} from '../../TierRestriction/utils';
import {useIsFeatureInTier} from '../../../utils/component/index';
import {useDeleteToggleProductSelection} from '../utils/bulkEditOptions';
import {useSearchProductEnabled} from '../../products/hooks/useSearchProductEnabled';
import {useStoreBusinessModel} from '../../store/utils';

const useStyles = makeStyles(theme => ({
  root: {
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    [theme.breakpoints.up('lg')]: {
      minHeight: 'calc(100vh - 232px)',
      padding: '2rem',
    },
  },
  productAddButton: {
    textTransform: 'none',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    borderRadius: '.5rem',
    fontSize: '.9rem',
  },
  content: {
    background: 'white',
    width: '100%',
    height: '100%',
    borderRadius: '.75rem',
    border: '1px solid #F4F4F6',
    position: 'relative',
  },
  contentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'center',
    padding: '1rem',
    gap: '1rem',
    flexWrap: 'wrap',
    borderBottom: `1px solid ${colors.grey200}`,
  },
  headerFilters: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  filterItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '250px',
    borderBottom: `1px solid ${colors.grey200}`,
    padding: '0.75rem 1.5rem',

    '&:last-child': {
      borderBottom: 'none',
    },
  },
  filterLabel: {
    color: colors.grey900,
    fontWeight: 600,
    fontSize: '.9rem',
    maxWidth: '18ch',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  customCheckbox: {
    borderRadius: '50%',
    width: '1.5rem',
    height: '1.5rem',
    border: `1.25px solid ${colors.grey400}`,
    background: 'white',

    '&[data-checked="true"]': {
      border: `6px solid ${colors.primary}`,
    },
  },

  tableContainerWrap: {
    position: 'relative',
    minHeight: '610px',
  },
  tableContainer: {
    marginBottom: '1rem',
    position: 'relative',
    scrollBehavior: 'smooth',
    overflow: 'initial',

    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
    },

    '&::-webkit-scrollbar-track': {
      background: colors.grey200,
      borderRadius: '5px',
    },

    '&::-webkit-scrollbar-thumb': {
      background: colors.grey400,
      borderRadius: '5px',
    },
  },
  tableArrows: {
    position: 'sticky',
    height: 0,
    top: '350px',
    width: '100%',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 1,
  },
  tableArrow: {
    padding: '.75rem',
  },
  tableHeader: {
    background: colors.grey300,
  },
  tableBody: {
    '&  td': {
      padding: '.5rem',
    },
  },

  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '1rem',
    padding: '1rem 0',
  },

  tableLoader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '6rem',
  },
  headerWrapper: {
    maxWidth: '100%',
  },
}));

// const getPriceWithTaxes = (price: number, taxFactor: number) => parseFloat(price * taxFactor).toFixed(5);
const getPriceWithoutTaxes = (price: string, taxFactor: number) =>
  parseFloat(Number(price) / taxFactor).toFixed(5);

const transformBulkEditProduct = (p: Product): any => {
  const taxes = (p.taxes ?? [])
    .map(parseFloat)
    .reduce((acc, curr) => acc + curr, 1);

  const price = getPriceWithoutTaxes(p.price, taxes);
  const taxedPrice = p.price;

  const offerPrice = p.offerPrice
    ? getPriceWithoutTaxes(p.offerPrice, taxes)
    : '';
  const taxedOfferPrice = p.offerPrice ? p.offerPrice : '';

  return {
    ...p,
    price,
    taxedPrice,
    offerPrice,
    taxedOfferPrice,
    taxes: p.taxes,
    categoryTaxes: [],
    subCategoryItems: p.subCategoryItems || [],
  };
};

export default function BulkEdit() {
  const classes = useStyles();

  const tableRef = useRef<HTMLElement | null>(null);

  const {isFetchingProducts, products} = useProducts();
  const pagination = useProductPagination();
  const [peerPagePagination, setPeerPagePagination] = useState(8);
  const [querySearch, setQuerySearch] = useState('');
  const {showTierRestrictionModal} = useTierRestrictionActions();
  const removeAllToggleProductSelection = useDeleteToggleProductSelection();
  const hasBulkEditFeature = useIsFeatureInTier('PRODUCTS_BULK_EDIT');

  const businessModel = useStoreBusinessModel();

  const {getProducts} = useFetchProducts();

  const searchProductEnabled = useSearchProductEnabled();

  const handleTableClick = e => {
    if (!hasBulkEditFeature) {
      showTierRestrictionModal();
    }
  };

  useEffect(() => {
    removeAllToggleProductSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (querySearch === '' || !querySearch) {
      getProducts({
        size: peerPagePagination,
      });
      return;
    }

    getProducts({
      size: peerPagePagination,
      name: querySearch,
    });
  }, [getProducts, peerPagePagination, querySearch]);

  return (
    <TableCellProvider>
      <div className={classes.root}>
        <PageHeader
          title="Editor de productos"
          backButton
          classes={{
            headerWrapper: classes.headerWrapper,
          }}
          rightContent={
            <CustomButton
              color="primary"
              className={classes.productAddButton}
              variant="outlined"
              to="/products/new"
            >
              <span>
                Agregar {businessModel === 'PRODUCT' ? 'producto' : 'servicio'}
              </span>
              <Add />
            </CustomButton>
          }
        />

        <section className={classes.content}>
          <header className={classes.contentHeader}>
            <div className={classes.searchContainer}>
              <SearchBar
                placeholder="Buscar producto"
                color="#F4F4F6"
                disabled={!searchProductEnabled}
                externalHandleSearch={query => {
                  if (query === '' || !query) {
                    setQuerySearch('');
                    getProducts();
                    return;
                  }
                  setQuerySearch(query);
                  getProducts({
                    name: query || querySearch,
                    size: peerPagePagination,
                  });
                }}
              />
            </div>
            <div className={classes.headerFilters}>
              <TableCellFilter />
              {/* <BulkEditOptions /> */}
            </div>
          </header>

          <ProductFilter />

          <div className={classes.tableContainerWrap}>
            {/*  {products.length > 0 && (
              <div className={classes.tableArrows}>
                <IconButton
                  className={classes.tableArrow}
                  data-direction="left"
                  onClick={handleScrollLeft}
                >
                  <ArrowBackIosSharp fontSize="small" />
                </IconButton>
                <IconButton
                  className={classes.tableArrow}
                  data-direction="right"
                  onClick={handleScrollRight}
                >
                  <ArrowForwardIos fontSize="small" />
                </IconButton>
              </div>
            )} */}
            {isFetchingProducts ? (
              <div className={classes.tableLoader}>
                <CircularProgress />
              </div>
            ) : (
              <CustomHorizontalScroll
                classes={{
                  arrowButton: classes.tableArrow,
                }}
              >
                <TableContainer
                  className={classes.tableContainer}
                  ref={tableRef}
                >
                  <Table onClick={handleTableClick}>
                    <TableHead className={classes.tableHeader}>
                      <TableHeader />
                    </TableHead>
                    <TableBody className={classes.tableBody}>
                      {products.map(product => (
                        <ProductProvider
                          key={product.id}
                          product={transformBulkEditProduct(product)}
                        >
                          <ProductRow />
                        </ProductProvider>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CustomHorizontalScroll>
            )}
          </div>
          {pagination && (
            <div className={classes.pagination}>
              <Pagination
                count={
                  pagination &&
                  (pagination.size >= pagination.total
                    ? 1
                    : Math.ceil(pagination.total / pagination.size))
                }
                page={pagination.page + 1}
                onChange={(e, page) => {
                  if (querySearch === '') {
                    getProducts({
                      page: page - 1,
                      size: peerPagePagination,
                    });
                    return;
                  }
                  getProducts({
                    page: page - 1,
                    name: querySearch,
                    size: peerPagePagination,
                  });
                }}
                color="primary"
              />
              <CustomSelect
                pagination
                disableUnderline
                className={classes.sizeSelect}
                value={peerPagePagination}
                onChange={e => {
                  setPeerPagePagination(e.target.value);
                }}
              >
                <MenuItem value={8}>8 por página</MenuItem>
                <MenuItem value={16}>16 por página</MenuItem>
                <MenuItem value={24}>24 por página</MenuItem>
                <MenuItem value={32}>32 por página</MenuItem>
              </CustomSelect>
            </div>
          )}
        </section>
      </div>
      <BulkEditOptions />
      <BulkEditToast />
      {/* <Toast open={success} severity="success" color="success">
        Producto editado correctamente
      </Toast>
      <Toast open={error} severity="error" color="error">
        Error al editar el producto, refresca la página y vuelve a intentarlo
      </Toast> */}
    </TableCellProvider>
  );
}
